import { useState, useRef, useEffect } from "react";
import {
  Badge,
  Kicker,
  Menu,
  MenuItem,
  MenuSeparator,
  Status,
  Thinking,
} from "@fwf/fwfa-design-system/react";
import { AGENT_PRESETS } from "@/lib/agent-configs";
import type { ModelInfo, HarnessState, ConnectionStatus } from "@/types";

interface Props {
  selectedAgent: string | null;
  status: ConnectionStatus;
  availableModels: ModelInfo[];
  currentModelId: string | null;
  isRefreshingModels: boolean;
  harnessStates: HarnessState[];
  onChangeAgent: (agentId: string) => void;
  onChangeModel: (modelId: string) => void;
  onRefreshModels: () => void;
  uiVariant?: "default" | "desktop" | "web";
}

// Short display name for the agent
const AGENT_SHORT: Record<string, string> = {
  copilot: "Copilot",
  codex: "Codex",
  claude: "Claude Code",
  gemini: "Gemini",
  opencode: "OpenCode",
};

// Chevron icon
function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m19.5 8.25-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

// Connection status in the DS's Status vocabulary. `ready` is the one live state,
// so it takes the breathing dot; error alerts, and the in-progress states are
// covered by the spinner branch below rather than by a tone.
const CONNECTION_TONE: Record<
  string,
  { tone?: "success" | "info" | "warning" | "danger"; live?: boolean }
> = {
  disconnected: {},
  installing: { tone: "warning" },
  connecting: { tone: "warning" },
  authenticating: { tone: "warning" },
  ready: { tone: "success", live: true },
  error: { tone: "danger" },
};

// Harness status → the same vocabulary, for the per-agent row in the menu.
const HARNESS_TONE: Record<
  HarnessState["status"],
  { label: string; tone?: "success" | "danger"; live?: boolean }
> = {
  running: { label: "running", tone: "success", live: true },
  installed: { label: "installed" },
  not_installed: { label: "not installed" },
  error: { label: "error", tone: "danger" },
};

// Whether a status represents an in-progress connection attempt
function isConnectingStatus(status: ConnectionStatus): boolean {
  return status === "connecting" || status === "installing" || status === "authenticating" || status === "authenticating:terminal";
}

export function HarnessModelSelector({
  selectedAgent,
  status,
  availableModels,
  currentModelId,
  isRefreshingModels,
  harnessStates,
  onChangeAgent,
  onChangeModel,
  onRefreshModels,
  uiVariant = "default",
}: Props) {
  const isDesktop = uiVariant === "desktop";
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const agentLabel = selectedAgent
    ? AGENT_SHORT[selectedAgent] || AGENT_PRESETS[selectedAgent]?.name || selectedAgent
    : "No Agent";

  const currentModel = availableModels.find((m) => m.modelId === currentModelId);
  const modelLabel = currentModel?.name || currentModelId || "Default";

  const harnessStatus = (presetId: string): HarnessState["status"] => {
    return harnessStates.find((h) => h.presetId === presetId)?.status ?? "not_installed";
  };

  const connection = CONNECTION_TONE[status] || CONNECTION_TONE.disconnected;

  return (
    <div className="flex min-w-0 items-center">
      <div ref={containerRef} className="relative">
        {/* Trigger pill: [dot] Agent  Model [chevron] */}
        <button
          onClick={() => {
            if (!menuOpen) onRefreshModels();
            setMenuOpen(!menuOpen);
          }}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="group inline-flex h-8 min-w-0 items-center gap-1.5 rounded-lg px-2 py-1 text-left transition-colors hover:bg-surface-hover active:translate-y-px"
        >
          {isConnectingStatus(status) ? (
            /* Thinking brings its own aria-live announcement and reduced-motion
               handling, which the hand-rolled spinner had neither of. */
            <Thinking label={`${agentLabel} connecting`} className="flex-none" />
          ) : (
            /* Status with no children is the dot alone: the label beside it is
               the agent name, not the state word. */
            <Status
              tone={connection.tone}
              live={connection.live}
              aria-label={`Agent ${status}`}
              className="flex-none"
            >
              {null}
            </Status>
          )}
          <span className="max-w-[160px] truncate font-body text-xs font-medium text-ink sm:max-w-[200px]">
            {agentLabel}
          </span>
          {availableModels.length > 0 && !isRefreshingModels && (
            <span className="max-w-[220px] truncate font-body text-xs text-ink-muted sm:max-w-[340px]">
              {modelLabel}
            </span>
          )}
          {isRefreshingModels && (
            <span className="font-body text-xs text-ink-muted">Refreshing...</span>
          )}
          <ChevronDown className="h-2.5 w-2.5 text-ink-muted transition-colors group-hover:text-ink-soft" />
        </button>

        {/* Unified dropdown: agents + models. Menu owns the frame, the opaque
            fill, the float shadow, and its own max-height with scroll, so the
            only thing left here is where it hangs and how wide it is. */}
        {menuOpen && (
          <Menu
            label="Agent and model"
            className={`absolute left-0 top-full z-50 mt-1 origin-top-left animate-scale-in ${
              isDesktop ? "w-96" : "w-80"
            }`}
          >
            <Kicker className="mb-0 px-3 pb-1 pt-2">Agent</Kicker>
            {Object.entries(AGENT_PRESETS)
              .filter(([id]) => id !== "custom")
              .map(([id, preset]) => {
                const isActive = selectedAgent === id;
                const harness = HARNESS_TONE[harnessStatus(id)];
                return (
                  <MenuItem
                    key={id}
                    selected={isActive}
                    onClick={() => {
                      onChangeAgent(id);
                      setMenuOpen(false);
                    }}
                    // Two-line item, so the row lays out from the top rather
                    // than centring a tall block against a one-line status.
                    className="items-start py-2"
                    // The meta slot styles its own text (quiet mono micro-caps
                    // for repeated row metadata), so the tone rides the dot and
                    // the word stays calm. That is the register the DS wants
                    // here, and it is why the label is not fighting for colour.
                    meta={
                      <Status tone={harness.tone} live={harness.live}>
                        {harness.label}
                      </Status>
                    }
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex items-center gap-1.5">
                        <span
                          className={`truncate font-display text-sm font-medium ${
                            preset.acpSupport === "none" ? "text-ink-muted" : "text-ink"
                          }`}
                        >
                          {preset.name}
                        </span>
                        {preset.acpSupport === "none" && (
                          <Badge variant="soft">no ACP</Badge>
                        )}
                        {preset.acpSupport === "experimental" && (
                          <Badge variant="accent">beta</Badge>
                        )}
                      </span>
                      <span
                        className={`font-body text-xs text-ink-muted ${
                          isDesktop ? "" : "truncate"
                        }`}
                      >
                        {preset.description}
                      </span>
                    </span>
                  </MenuItem>
                );
              })}

            {/* Model section — only when models are available */}
            {availableModels.length > 0 && (
              <>
                <MenuSeparator />
                <Kicker className="mb-0 px-3 pb-1 pt-1">Model</Kicker>
                {availableModels.map((model) => (
                  <MenuItem
                    key={model.modelId}
                    selected={model.modelId === currentModelId}
                    onClick={() => {
                      onChangeModel(model.modelId);
                      setMenuOpen(false);
                    }}
                    className="items-start py-2"
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate font-body text-sm font-medium text-ink">
                        {model.name}
                      </span>
                      {model.description && (
                        <span
                          className={`font-body text-xs text-ink-soft ${
                            isDesktop ? "" : "truncate"
                          }`}
                        >
                          {model.description}
                        </span>
                      )}
                      <span className="truncate font-mono text-xs text-ink-muted">
                        {model.modelId}
                      </span>
                    </span>
                  </MenuItem>
                ))}
              </>
            )}
          </Menu>
        )}
      </div>
    </div>
  );
}
